import { Formik } from "formik";
import {
  ClientData,
  RatecardFdpInvoiceResponse,
} from "../../../../app/models/responseModels/FdpInvoiceResponses";
import Loader from "../../../../components/common/loader/Loader";
import { Autocomplete, Grid, Typography } from "@mui/material";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ClientInvoiceEditResponse,
  ClientInvoiceWithDateRange,
  RatecardClientInvoiceResponse,
  RouteResponse,
  TripAndDateRangeDataForSelectedStores,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import { ClientInvoiceAgent } from "../../../../app/api/clientInvoices/ClientInvoiceAgent";
import dayjs from "dayjs";
import { getMinOrMaxDate } from "../../../clients/invoices/utils/ClientInvoiceUtils";
import getAxiosError from "../../../../app/api/axiosError";
import { FdpInvoiceAgent } from "../../../../app/api/fdpInvoices/FdpInvoiceAgent";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import EditInvoiceDetailsModal from "../../../clients/invoices/modals/EditInvoiceDetailsModal";
import { formatDate } from "../../../../components/common/utils/DateUtils";
import ClientInvoiceEditList from "../../../clients/invoices/modals/ClientInvoiceEditList";

export type FdpInvoiceEditProps = {
  fdpId: number;
  fdpInvoice: RatecardFdpInvoiceResponse | undefined;
  closeInvoiceEditModal: CallableFunction;
  onAddMoreStores: CallableFunction;
  clients: ClientData[];
};

const FdpInvoiceEditModal = (props: FdpInvoiceEditProps) => {
  const initialValues = {
    stores: [] as number[],
    client: undefined,
    storeSelectType: "single",
    start_date: undefined,
    end_date: undefined,
    route: undefined,
  };


  const validationSchema = Yup.object().shape({
    stores: Yup.array()
      .min(1, "Please select atleast 1 store")
      .required("Please select a store"),
    end_date: Yup.date().when("start_date", (start_date, schema) => {
      if (start_date != null && start_date[0] != null)
        return schema
          .required("Please select the end date")
          .min(Yup.ref("start_date"), "End date must be after start date");
      return schema.optional();
    }),
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [selectedRoute, setSelectedRoute] = useState<RouteResponse | null>(
    null
  );

  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientInvoice, setSelectedClientInvoice] = useState<
    RatecardClientInvoiceResponse | undefined
  >();

  const [editList, setEditList] = useState<ClientInvoiceEditResponse[]>([]);
  const [deleteInvoiceEditIds, setDeleteInvoiceEditIds] = useState<number[]>(
    []
  );

  const [clientInvoiceIdForDelete, setClientInvoiceIdForDelete] =
    useState<string>();

  const [selectedRatecardId, setSelectedRatecardId] = useState<number>();
  const [currentEditId, setCurrentEditId] = useState<number | null>(null);

  const [routes, setRoutes] = useState<RouteResponse[]>([]);

  const [startRange, setStartRange] = useState<any>();
  const [endRange, setEndRange] = useState<any>();
  const [selectedStores, setSelectedStores] = useState<string>();

  const [tripAndDateRangeData, setTripAndDateRangeData] = useState<
    TripAndDateRangeDataForSelectedStores[]
  >([]);
  const [clientsWithDateRanges, setClientsWithDateRanges] = useState<
    ClientInvoiceWithDateRange[]
  >([]);

  //TODO : need to modify for FDP Edits if require
  const handleOpenEdit = (
    currentEditId: number,
    store: number,
    tripId: string | null
  ) => {
    // when we select an existing to edit on we need to fill these for the details modal to work
    setCurrentEditId(currentEditId);
    setSelectedStores(`${store}`);

    // we get store id from the list and we find the ratecard id from the list of stores we have
    const rcId = props.fdpInvoice?.stores.find(
      (s) => s.id === store
    )?.ratecardId;
    setSelectedRatecardId(rcId);

    if (tripId) fetchRouteByTripId(tripId);

    // for fdp invoice edit get the client invoice id
    const edit = editList.find((edit) => edit.id === currentEditId);
    // primary key for client invoice id
    const invoiceId = clientsWithDateRanges.filter(
      (data) => data.clientId === edit?.clientId
    )[0].id;

    fetchClientInvoice(invoiceId).then(() => {
      openEditInvoiceDetailsModal();
    });
  };

  const fetchEditList = async () => {
    if (props.fdpInvoice?.id) {
      try {
        const response = await FdpInvoiceAgent.fetchAllEditLists(
          props.fdpInvoice?.id,
          props?.fdpInvoice?.fdpId
        );

        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "getEditDataByFdpInvoiceId",
          });
          return;
        } else {
          setEditList(
            response.data.map((invoice) => {
              // Find the corresponding client from clientData based on clientId
              const client = props.clients.find(
                (client) => client.clientId === invoice.clientId
              );

              // If client is found, map it to the invoice object, otherwise set name to 'Unknown'
              // If client is found, add the clientName to the invoice object
              if (client) {
                invoice.clientName = client.clientName;
              } else {
                invoice.clientName = "Unknown"; // Set fallback value if client is not found
              }
              return invoice;
            })
          );
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "getRatecardByIdException",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteInvoiceEdit = async () => {
    try {
      if (props.fdpInvoice) {
        const response = await ClientInvoiceAgent.deleteInvoiceEdit(
          clientInvoiceIdForDelete ?? "",
          deleteInvoiceEditIds.join(","),
          props.fdpInvoice.createdOn
        );

        console.log(`handleDeleteInvoiceEdit`);
        console.log(`response = ${JSON.stringify(response)}`);

        if (response.isSuccess) {
          console.log(`refresh edit list`);
          // refresh edit list
          setEditList(response.data);
        } else {
          toast.error(response.message, {
            toastId: "tripAndDateRangeData",
          });
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "tripAndDateRangeDataException",
      });
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  const getClientsWithDateRanges = async () => {
    try {
      if (props.fdpInvoice) {
        const response = await FdpInvoiceAgent.getClientsWithDateRanges(
          props.fdpInvoice.id
        );

        setClientsWithDateRanges(response.data);

        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "tripAndDateRangeData",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "tripAndDateRangeDataException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTripAndDateRangeForSelectedStores = async () => {
    try {
      if (props.fdpInvoice && selectedStores) {
        const response =
          await FdpInvoiceAgent.getTripAndDateRangeForSelectedStores(
            props.fdpInvoice.fdpInvoiceId,
            selectedStores
          );

        setTripAndDateRangeData(response.data);

        if (response.data && response.data.length > 0) {
          const r: RouteResponse[] = response.data.map((item) => item.route);
          console.log(`routes = ${JSON.stringify(r)}`);
          setRoutes(r);

          const startDateRangeList = response.data.map((item) =>
            dayjs(item.startDateRange)
          );

          const endDateRangeList = response.data.map((item) =>
            dayjs(item.endDateRange)
          );

          setStartRange(getMinOrMaxDate(startDateRangeList, true));
          setEndRange(getMinOrMaxDate(endDateRangeList, false));
        }

        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "tripAndDateRangeData",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "tripAndDateRangeDataException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientInvoice = async (id: number) => {
    try {
      if (props.fdpInvoice) {
        const response = await ClientInvoiceAgent.getClientInvoice(id);

        if (response.isSuccess && response.data) {
          setSelectedClientInvoice(response.data);
        } else {
          toast.error(response.message, {
            toastId: "getClientInvoiceException",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "getClientInvoiceException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getClientsWithDateRanges();
  }, []);

  useEffect(() => {
    if (selectedStores) {
      setIsLoading(true);
      fetchTripAndDateRangeForSelectedStores();
    }
  }, [selectedStores]);

  const fetchRouteByTripId = async (tripId: string) => {
    try {
      if (props.fdpInvoice) {
        const response = await ClientInvoiceAgent.getGenericRouteByTripId(
          props.fdpInvoice?.id,
          tripId
        );

        if (response.isSuccess && response.data) {
          console.log(
            `fetchRouteByTripId = selected route = ${JSON.stringify(
              response.data
            )}`
          );
          setSelectedRoute(response.data);
        } else {
          toast.error(response.message, {
            toastId: "getRouteByTripIdException",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "getRouteByTripIdException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ***** UseEffects *****
  useEffect(() => {
    fetchEditList();
    // fetchStoresForInvoice();
  }, []);
  useEffect(() => {
    setIsLoading(true);
    getClientsWithDateRanges();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      const matchingClients = clientsWithDateRanges.filter(
        (clientData) => clientData.clientId === selectedClientId
      );
      
      if (matchingClients.length > 0) {
        const invoiceId = matchingClients[0].id;
        setIsLoading(true);
        fetchClientInvoice(invoiceId);
      }
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedStores) {
      setIsLoading(true);
      fetchTripAndDateRangeForSelectedStores();
    }
  }, [selectedStores]);

  // open edit invoice modal logic
  const [editInvoiceDetailsModal, openEditInvoiceDetailsModal] = useSubmitModal(
    (closeEditInvoiceDetailsModal) =>
      selectedRatecardId ? (
        <EditInvoiceDetailsModal
          store={selectedStores ?? ""}
          clientId={selectedClientId ?? 0}
          startDate={startDate ? formatDate(startDate.toDate()) : null}
          endDate={endDate ? formatDate(endDate.toDate()) : null}
          route={selectedRoute}
          ratecardId={selectedRatecardId}
          closeEditInvoiceDetailsModal={props.onAddMoreStores}
          // closeEditInvoiceDetailsModal={() => {
          //   closeEditInvoiceDetailsModal();
          //   props.closeInvoiceEditModal();
          // }}
          invoice={selectedClientInvoice}
          onCompleted={() => {
            props.closeInvoiceEditModal();
          }}
          onAddNewStores={props.onAddMoreStores}
          currentEditId={currentEditId}
          isFdpInvoiceEdit={true}
          fdpInvoiceId={props.fdpInvoice?.id ?? null}
          fdpInvoiceStatus={props.fdpInvoice?.invoiceStatus ?? null}
        />
      ) : undefined
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {
          openEditInvoiceDetailsModal();
        }}
      >
        {({
          values,
          setFieldValue,
          isValid,
          errors,
          handleChange,
          handleBlur,
        }) => {
          return (
            <SubmitModalWithFormik
              title="Edit FDP invoice"
              closeSubmitModal={props.closeInvoiceEditModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="lg"
              isLoading={false}
              submitButtonName="Edit"
              closeModalAfterSubmit={false}
              fullWidth={true}
            >
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                alignItems={"center"}
              >
                <Loader isLoading={isLoading} />

                <Grid item xs={values.stores.length > 1 ? 4 : 3}>
                  <Autocomplete
                    id="client"
                    options={props.clients ?? []}
                    limitTags={3}
                    disableCloseOnSelect
                    size="small"
                    onChange={(_, value) => {
                      if (value) {
                        setFieldValue("client", value.clientId);
                        setSelectedClientId(value.clientId);
                      }
                    }}
                    value={props.clients.find(
                      (x) => values.client === x.clientId
                    )}
                    getOptionLabel={(option) => `${option.clientName}`}
                    sx={{
                      // overring font-family else it chose different font-family on its own!
                      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                    }}
                    renderInput={(params) => (
                      <FormikTextField
                        {...params}
                        margin="normal"
                        name="clients"
                        label="Select client to edit"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={values.stores.length > 1 ? 4 : 3}>
                  <Autocomplete
                    multiple
                    id="stores"
                    options={
                      props.fdpInvoice?.stores.filter(
                        (store) => store.clientId === values.client
                      ) ?? []
                    }
                    limitTags={3}
                    disableCloseOnSelect
                    disabled={values.client === undefined}
                    size="small"
                    onChange={(event, value) => {
                      setFieldValue(
                        "stores",
                        value.map((x) => x.id)
                      );

                      // if no option is selected then revert selectedRatecardId to undefined
                      if (!value || !value[0]) {
                        setSelectedRatecardId(undefined);
                      }

                      // if selecting the first store in the list then set selectedRatecardId so that stores of other ratecards are disabled
                      if (
                        values.stores.length === 0 &&
                        value &&
                        value[0].ratecardId
                      )
                        setSelectedRatecardId(value[0].ratecardId);

                      // convert selected store values to string of comma-separated store ids to be used for fetching the routes for these stores
                      const storeList = value.map((x) => x.id).toString();
                      setSelectedStores(storeList);
                      // TODO: clear route field on store select

                      setRoutes([]);
                    }}
                    value={props.fdpInvoice?.stores.filter((x) =>
                      values.stores?.includes(x.id)
                    )}
                    getOptionDisabled={(option) => {
                      // select store that belong to same ratecard only
                      // disable other stores
                      if (
                        selectedRatecardId &&
                        option.ratecardId !== selectedRatecardId
                      )
                        return true;
                      return false;
                    }}
                    getOptionLabel={(option) => `${option.storeId}`}
                    sx={{
                      // overring font-family else it chose different font-family on its own!
                      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                    }}
                    renderInput={(params) => (
                      <FormikTextField
                        {...params}
                        margin="normal"
                        name="stores"
                        label="Select stores to edit"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  display={values.stores.length > 1 ? "none" : ""}
                >
                  <Autocomplete
                    id="route"
                    options={routes}
                    size="small"
                    disabled={values.stores.length === 0}
                    getOptionLabel={(option) => `${option.tripId}`}
                    sx={{
                      // overring font-family else it chose different font-family on its own!
                      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                    }}
                    onChange={(event, value) => {
                      setFieldValue("route", value?.id);
                      setSelectedRoute(value);
                    }}
                    value={routes.find((x) => values.route === x.id)}
                    renderInput={(params) => (
                      <FormikTextField
                        {...params}
                        margin="normal"
                        name="route"
                        label="Select a route to edit"
                        size="small"
                        fullWidth
                        variant="outlined"
                        slotprops={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                <Loader isLoading={true} />
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <>
                  <Grid item container>
                    <Typography component="h4" variant="h4">
                      Edit List
                    </Typography>
                  </Grid>

                  <Grid item>
                    <ClientInvoiceEditList
                      editList={editList}
                      isClientIdColumnVisible={true}
                      handleOpenEdit={handleOpenEdit}
                      handleDeleteInvoiceEdit={handleDeleteInvoiceEdit}
                      setDeleteInvoiceEditIds={setDeleteInvoiceEditIds}
                      setDeleteClientInvoiceId={setClientInvoiceIdForDelete}
                      invoice={props.fdpInvoice}
                    />
                  </Grid>
                </>
              </Grid>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
      {editInvoiceDetailsModal}
    </>
  );
};
export default FdpInvoiceEditModal;
